import router from "@/router";
import { SYSTEM_CONFIG } from "@/config/app.config";
import { MOBILE_PAGE_HISTORY_MODE_LIST } from "@/config/router.config";
import { useUserStore } from "@/stores/user.store";
import { useGrowthBookStore } from "@/stores/growthBook.store";
import { useProfileFormStore } from "@/stores/accountManagement.store";
import { useLayoutStore } from "@/stores/layout.store";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { createTitlePage } from "@/utils/layout.util";
import { logRollbarError } from "@/utils/rollbar.util";
import { checkCookie } from "@/utils/cookie.util";
import { setStorageBookingPreviousQueryParam } from "@/utils/storage.util";
import { setPage } from "@/utils/requestManager.util";
import { growthbook } from "@/plugins/growthbook";

NProgress.configure({ showSpinner: false });

router.beforeEach(async (to, from, next) => {
  // track the latest page
  // Use parent route name if available so sub-routes don't cancel each other's requests
  if (to.name) {
    setPage(to.name);
  }

  // Set the app bar title
  const layoutStore = useLayoutStore();
  if (MOBILE_PAGE_HISTORY_MODE_LIST.includes(to.name)) {
    layoutStore.setHistoryMode(true);
  } else {
    layoutStore.setHistoryMode(false);
  }

  const userStore = useUserStore();

  if (from.name === "booking") {
    if (from.query) {
      setStorageBookingPreviousQueryParam(JSON.stringify(from.query));
    }
  }

  // Start progress bar
  NProgress.start();

  if (handleSpecialQueryParams(to, userStore, next)) {
    return;
  }

  if (userStore.getToken) {
    await handleAuthenticatedUser(userStore, to, next);
  } else {
    handleUnauthenticatedUser(to, next);
  }
});

router.afterEach((to) => {
  // title page on side bar
  createTitlePage(to);
  NProgress.done();
});

function handleSpecialQueryParams(to, userStore, next) {
  if (to.query.resetPasswordToken) {
    userStore.logOut();
  }

  if (to.query.language) {
    window.localStorage.setItem("locale", to.query.language);
  }

  if (to.query.token) {
    if (to.name !== "admin-package-submission") {
      return handleTokenLogin(to, userStore, next);
    }
  }

  return false;
}

async function handleTokenLogin(to, userStore, next) {
  try {
    const fetchUserInfo = await userStore.getUserInfo(to.query.token);
    if (fetchUserInfo) {
      next({ path: to.path });
      NProgress.done();
      return true;
    }
  } catch (e) {
    console.warn("Login via token denied!");
    next(`/login`);
    NProgress.done();
    return true;
  }

  return false;
}

async function handleAuthenticatedUser(userStore, to, next) {
  if (!checkCookie()) {
    userStore.logOut();
    window.location.href = "/unauthorized-401";
  }

  if (SYSTEM_CONFIG.deniedListsName.includes(to.name)) {
    next({ path: "/" });
    NProgress.done();
    return;
  }

  await ensureUserIsLoaded(userStore, to);

  if (userStore.getUser) {
    if (to.meta?.requiredRole && userStore.role !== to.meta.requiredRole) {
      next({ name: "access-restricted-403" });
      NProgress.done();
      return;
    }
    checkFeatureEnabled(userStore, to, next);
    next();
  } else {
    handleUserLoadFailure(to, next);
  }
}

async function ensureUserIsLoaded(userStore, to) {
  if (!userStore.getUser) {
    try {
      await userStore.getUserInfo();
    } catch (e) {
      console.log("permission::get user details", e);
      const errorMessage = e.message ?? "Error permission::get user details";
      logRollbarError(errorMessage);
    }
  } else {
    if (userStore.getRestaurants.length === 0) {
      userStore.storeUser(userStore.getUser);
    }
    if (userStore.getRestaurants.length === 0) {
      userStore.storeUser(userStore.getUser);
    }
  }

  if (userStore.restaurants.length === 0) {
    try {
      await userStore.storeUser(userStore.getUser);
    } catch (e) {
      console.log("permission::get restaurants", e);
    }
  }

  const accountStore = useProfileFormStore();
  if (!accountStore.storeDetailsData && to.name !== "accountManagement-store") {
    const restaurantId =
      to.query.restaurantId && to.query.restaurantId !== "all" ? to.query.restaurantId : undefined;
    try {
      await accountStore.getStoreDetail(restaurantId);
    } catch (e) {
      console.log("permission::get restaurant info", e);
      const errorMessage = e.message ?? "Error permission::get restaurant info";
      logRollbarError(errorMessage);
    }
  }
}

function handleUserLoadFailure(to, next) {
  next({ name: "unauthorized-401" });
  window.localStorage.clear();
}

function handleUnauthenticatedUser(to, next) {
  if (SYSTEM_CONFIG.whiteListsName.includes(to.name)) {
    next();
  } else {
    const allRoutes = router.getRoutes();
    if (allRoutes.some((route) => route.path === to.path)) {
      next(`/login?redirect=${to.path}`);
    } else {
      next({ name: "not-found-fallback" });
    }
    NProgress.done();
  }
}

function checkFeatureEnabled(userStore, to, next) {
  const accountStore = useProfileFormStore();
  const growthBookStore = useGrowthBookStore();

  const countryCode = accountStore.currencyCode?.toUpperCase() || "TH";

  const attributes = {
    email: userStore.email,
    countryCode: countryCode,
  };

  if (userStore.restaurantID != null) {
    attributes.id = String(userStore.restaurantID);
  }

  growthbook.setAttributes(attributes);
  growthBookStore.newsAndUpdateDashboard = growthbook.isOn("book-bite-news-dashboard-feature");

  growthBookStore.marketingPage = growthbook.isOn("book-bite-marketing-feature");

  growthBookStore.voucherPage = growthbook.isOn("book-bite-voucher-feature");

  growthBookStore.billingPage = growthbook.isOn("book-bite-billing-feature");

  growthBookStore.accountingIntegration = growthbook.isOn(
    "book-bite-enable-accounting-integration-for-partner",
  );

  growthBookStore.pricingRulesPage = growthbook.isOn("book-bite-pricing-feature");

  if (
    typeof to.name === "string" &&
    to.name === "billing" &&
    !growthbook.isOn("book-bite-billing-feature")
  ) {
    return next({ name: "access-restricted-403" });
  }

  if (
    typeof to.name === "string" &&
    to.name === "billing-all-invoices" &&
    !growthBookStore.accountingIntegration
  ) {
    return next({ name: "access-restricted-403" });
  }

  if (
    typeof to.name === "string" &&
    to.name === "marketing" &&
    !growthbook.isOn("book-bite-marketing-feature")
  ) {
    return next({ name: "access-restricted-403" });
  }

  if (
    typeof to.name === "string" &&
    to.name === "voucher" &&
    !growthbook.isOn("book-bite-voucher-feature")
  ) {
    return next({ name: "access-restricted-403" });
  }

  if (
    typeof to.name === "string" &&
    to.name.includes("pricing-rules") &&
    !growthbook.isOn("book-bite-pricing-feature")
  ) {
    return next({ name: "access-restricted-403" });
  }
}
